import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Reveal, SectionLabel } from "../components/Reveal";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import api from "../lib/api";
import { useSiteServices } from "../hooks/useSiteServices";

const ADDONS = [
  { key: "false_ceiling", label: "False Ceiling", note: "₹85 / sq.ft" },
  { key: "modular_kitchen", label: "Premium Modular Kitchen", note: "₹1,80,000" },
  { key: "automation", label: "Home Automation", note: "₹1,20,000" },
];

const fmt = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

export default function Calculator() {
  const { packages } = useSiteServices();
  const [area, setArea] = useState(1200);
  const [pkg, setPkg] = useState("Modern");
  const [addons, setAddons] = useState({ false_ceiling: false, modular_kitchen: false, automation: false });
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({ name: "", mobile: "", email: "", city: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/estimate", { area, package: pkg, ...addons });
        setResult(data);
      } catch { /* ignore */ }
    }, 150);
    return () => clearTimeout(t);
  }, [area, pkg, addons]);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) { toast.error("Please add your name and mobile number."); return; }
    setSending(true);
    try {
      await api.post("/leads", {
        ...form, area, package: pkg, source: "calculator",
        message: `Estimate: ${result ? fmt(result.total) : "-"} | Add-ons: ${Object.keys(addons).filter((k) => addons[k]).join(", ") || "none"}`,
      });
      toast.success("Thank you! Our team will reach out with your detailed quotation.");
      setForm({ name: "", mobile: "", email: "", city: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <div className="pt-32 pb-24">
      <section className="max-w-7xl mx-auto px-6">
        <Reveal><SectionLabel>Estimation Calculator</SectionLabel></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif-display text-5xl md:text-6xl mt-6 max-w-3xl leading-tight">
            Your project cost, in real time.
          </h1>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 mt-14 grid lg:grid-cols-3 gap-8 items-start">
        {/* CONTROLS */}
        <div className="lg:col-span-2 space-y-10 rounded-3xl border border-border bg-card p-8 md:p-10">
          <div>
            <div className="flex items-end justify-between mb-6">
              <label className="label-caps text-muted-foreground">Total Area</label>
              <span data-testid="area-value" className="font-serif-display text-3xl text-accent">{area.toLocaleString("en-IN")} sq.ft</span>
            </div>
            <Slider data-testid="area-slider" value={[area]} min={200} max={8000} step={50} onValueChange={(v) => setArea(v[0])} />
            <div className="flex justify-between text-xs text-muted-foreground mt-2"><span>200</span><span>8,000</span></div>
          </div>

          <div>
            <label className="label-caps text-muted-foreground">Package</label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
              {packages.map((p) => (
                <button
                  key={p.name}
                  data-testid={`pkg-${p.name.toLowerCase()}`}
                  onClick={() => setPkg(p.name)}
                  className={`text-left rounded-2xl border p-4 transition-colors duration-300 ${
                    pkg === p.name ? "border-accent bg-accent/10" : "border-border hover:border-accent/60"
                  }`}
                >
                  <p className="font-serif-display text-xl">{p.name}</p>
                  <p className="text-sm text-muted-foreground">₹{p.rate.toLocaleString("en-IN")}/sq.ft</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-caps text-muted-foreground">Add-ons</label>
            <div className="mt-4 space-y-3">
              {ADDONS.map((a) => (
                <div key={a.key} className="flex items-center justify-between rounded-2xl border border-border p-4">
                  <div>
                    <p className="font-medium">{a.label}</p>
                    <p className="text-sm text-muted-foreground">{a.note}</p>
                  </div>
                  <Switch data-testid={`addon-${a.key}`} checked={addons[a.key]} onCheckedChange={(v) => setAddons((s) => ({ ...s, [a.key]: v }))} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RESULT */}
        <div className="lg:sticky lg:top-28 space-y-6">
          <motion.div layout className="rounded-3xl bg-primary text-primary-foreground p-8">
            <p className="label-caps text-accent">Estimated Cost</p>
            <p data-testid="estimate-total" className="font-serif-display text-5xl mt-3">
              {result ? fmt(result.total) : "—"}
            </p>
            <div className="mt-6 space-y-2 border-t border-primary-foreground/15 pt-5 text-sm">
              {result?.breakdown.map((b, i) => (
                <div key={i} className="flex justify-between gap-4">
                  <span className="text-primary-foreground/70">{b.label}</span>
                  <span className="whitespace-nowrap">{fmt(b.amount)}</span>
                </div>
              ))}
            </div>
            <p className="mt-5 text-xs text-primary-foreground/60">
              Final quotation confirmed after a site visit and detailed design discussion.
            </p>
          </motion.div>

          <form data-testid="lead-form" onSubmit={submitLead} className="rounded-3xl border border-border bg-card p-8 space-y-4">
            <p className="font-serif-display text-2xl">Get a detailed quote</p>
            {["name", "mobile", "email", "city"].map((f) => (
              <input
                key={f}
                data-testid={`lead-${f}`}
                value={form[f]}
                onChange={(e) => setForm((s) => ({ ...s, [f]: e.target.value }))}
                placeholder={{ name: "Full name*", mobile: "Mobile number*", email: "Email", city: "City / location" }[f]}
                className="w-full rounded-xl border border-border bg-transparent px-4 py-3 text-sm outline-none focus:border-accent transition-colors"
              />
            ))}
            <button data-testid="lead-submit" disabled={sending} className="w-full rounded-full bg-primary text-primary-foreground py-3.5 font-medium hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50">
              {sending ? "Sending…" : "Request Quotation"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
